import React, {
  useState, useMemo, useCallback, useEffect,
} from 'react';
import firebase from 'firebase/app';
import qs from 'qs';
import '../../../App.scss';
import 'antd/dist/antd.css';
import {
  Alert, Avatar, Button, Card, Space, Spin, Typography,
} from 'antd';
import { Redirect, useParams, useLocation, useHistory } from 'react-router-dom';
import {
  TeamOutlined,
  CheckCircleOutlined,
  LinkOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';

import CenteredSpinner from '../../../components/CenteredSpinner';
import { useCurrentUser } from '../../../models/useCurrentUser';
import { useClientForOwner } from '../../../models/useClientForOwner';
import { useTag } from '../../../models/useTag';
import { useTemplate } from '../../../models/useTemplate';
import { useClientDetails } from '../../../components/client/useClientDetails';
import { useTeamDoc } from '../../../components/client/useTeamDoc';
import { mergeClaimTagWithTemplate } from '../../../utils/mergeClaimTagWithTemplate';
import { getPayerBaseUrl } from '../../../utils/URLBuilder';

const { Text, Title, Paragraph } = Typography;

export const JOIN_GATE_COPY = 'This link is for staff at this business.';

/**
 * How long to wait for the members-map trigger before offering a way on manually.
 *
 * Normally about a second. Past this something is wrong with the trigger, and
 * holding someone on a spinner indefinitely is worse than letting them through
 * to a page that explains itself.
 */
export const JOIN_SETUP_SLOW_MS = 15000;

/**
 * Team Member Join screen — /client/join/:tagId?xmid=...
 *
 * A partner integration link hands staff members here (from the pay.strikepay.co
 * team page CTA). After client login, the member gives explicit consent to link
 * their partner staff ID (xmid) to their membership of the tag's team, via the
 * strike-payer join endpoint: POST {payer}/api/tags/{tagId}/join {xmid}
 * with the client's Firebase ID token.
 *
 * Sibling of ConfirmClaimTagScreen — same auth handling and visual patterns.
 */
export default function ConfirmJoinTeamScreen({ locStrings }) {
  const { tagId } = useParams();
  const location = useLocation();
  const history = useHistory();
  const queryParams = useMemo(
    () => qs.parse(location.search, { ignoreQueryPrefix: true }),
    [location.search],
  );
  const xmid = queryParams.xmid ? String(queryParams.xmid) : null;

  // ── Auth (only source of login truth) ──
  const [authLoading, authUser] = useCurrentUser();
  const uid = authUser?.uid;
  const isAnonymous = authUser?.isAnonymous;

  // ── Client (always subscribed via ownerId) ──
  const [clientLoading, client] = useClientForOwner(uid && !isAnonymous ? uid : null);

  // ── Tag + template (tag doc carries teamId/teamName; template supplies topLogo defaults) ──
  const [tagLoading, rawTag] = useTag(tagId);
  const [templateLoading, template] = useTemplate(rawTag?.templateTagId);
  const tag = useMemo(() => mergeClaimTagWithTemplate(rawTag, template), [rawTag, template]);

  // ── Local state ──
  const [joining, setJoining] = useState(false);
  // outcome: null | { kind: 'success', data } | { kind: <error-code-string> }
  const [outcome, setOutcome] = useState(null);

  const teamName = tag?.teamName || tag?.placeOfWork || tag?.displayName || 'this team';

  // Integration/partner display name (e.g. "OrbXi") — the xmid is the partner's
  // internal key (likely a UUID the staff member has never seen), so
  // member-facing copy leads with the partner and the link itself, never the
  // raw ID. The ID appears once, as small-print reference for support.
  const partner = useClientDetails(tag?.partnerClientId || null);
  const systemName = partner?.displayName || null;
  const recordHolder = systemName || 'your workplace system';

  // ── Consent → join call ──
  const handleConfirmJoin = useCallback(async () => {
    if (!xmid || !tagId) return;
    setJoining(true);
    try {
      const user = firebase.auth().currentUser;
      if (!user) throw new Error('Not signed in');
      const idToken = await user.getIdToken();
      const response = await fetch(`${getPayerBaseUrl()}/api/tags/${tagId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
        // Which client is consenting. The server verifies it against
        // clients/{id}.ownerId, so sending it grants nothing — it removes a
        // guess. Some uids own more than one client, and without this the server
        // cannot tell which and refuses rather than picking the wrong one.
        body: JSON.stringify({ xmid, clientId: client.id }),
      });
      let body = null;
      try { body = await response.json(); } catch (parseErr) { body = null; }

      if (response.ok) {
        // 200 = bound now, or already linked to this same ID (no-op) — both success.
        setOutcome({ kind: 'success', data: body || {} });
      } else {
        setOutcome({ kind: (body && body.error) || `http-${response.status}` });
      }
    } catch (err) {
      console.error('Join failed:', err);
      setOutcome({ kind: 'network-error' });
    } finally {
      setJoining(false);
    }
    // `client` is in here because the body now names it: the callback is created
    // on the first render, where client is still null, and without this it would
    // keep that null and throw on the click.
  }, [xmid, tagId, client]);

  // ── Waiting for the membership to land ──
  //
  // The join writes the member document. A trigger then adds this uid to the team
  // doc's `members` map, and that map is what every team rule reads — so until it
  // arrives the team page cannot render anything. Navigating on the join response
  // alone is what produced a flash of "not available to you" on a team the person
  // had, seconds earlier, joined.
  //
  // The team doc is readable by any signed-in user, so watching it needs no access
  // we do not already have, and it updates by subscription: no polling, no retry.
  // Subscribed from the start, not only after a successful join: the same
  // members map that gates navigation afterwards also decides the WORDS before
  // it. Someone already in the map is not "joining" — they are activating an
  // integration on a membership they already hold, and calling that "join"
  // reads as "am I not a member?".
  const { team: teamDoc } = useTeamDoc(tag?.teamId || null);
  const isMemberAlready = !!(uid && teamDoc && teamDoc.members && teamDoc.members[uid]);
  const joinedTeamId = outcome && outcome.kind === 'success' ? (tag?.teamId || null) : null;
  const membershipReady = !!(joinedTeamId && isMemberAlready);
  const [setupSlow, setSetupSlow] = useState(false);

  useEffect(() => {
    if (!joinedTeamId || membershipReady) return undefined;
    const timer = setTimeout(() => setSetupSlow(true), JOIN_SETUP_SLOW_MS);
    return () => clearTimeout(timer);
  }, [joinedTeamId, membershipReady]);

  const successClientId = (outcome && outcome.data && outcome.data.clientId) || client?.id;

  useEffect(() => {
    if (!membershipReady || !joinedTeamId || !successClientId) return;
    // justJoined still travels with it: the guard on the other side keeps its own
    // waiting state for anything that lands there before its listener catches up.
    history.push(`/client/manage/${successClientId}/teams/${joinedTeamId}`, { justJoined: true });
  }, [membershipReady, joinedTeamId, successClientId, history]);

  // ── Redirects (mirrors ConfirmClaimTagScreen: login carries the join URL) ──
  if (!authLoading && (!authUser || isAnonymous)) {
    const redirect = encodeURIComponent(location.pathname + location.search);
    return <Redirect to={`/client/login?redirect=${redirect}`} />;
  }

  // ── Loading ──
  const isLoading = authLoading || clientLoading || tagLoading
    || (rawTag?.templateTagId && templateLoading);

  // Signed in but no client yet: they just signed up on the way here and the
  // backend trigger is still creating the client. Transient — mirror
  // ConfirmClaimTagScreen rather than spinning silently forever.
  const waitingForClient = !clientLoading && !client && uid && !isAnonymous && tag;

  if (isLoading || !client) {
    if (waitingForClient) {
      return (
        <div style={{ padding: 48, textAlign: 'center' }}>
          <CenteredSpinner loading />
          <Paragraph type="secondary" style={{ marginTop: 16 }}>
            Setting up your account…
          </Paragraph>
          <Paragraph type="secondary" style={{ fontSize: 13, marginTop: 8 }}>
            This usually takes a few seconds. If it takes longer, try refreshing the page.
          </Paragraph>
        </div>
      );
    }
    return <CenteredSpinner loading />;
  }

  if (client.status === 'paused') {
    return <Redirect to="/" />;
  }

  const teamAvatar = (
    <Avatar
      src={tag?.topLogo || null}
      icon={<TeamOutlined />}
      size={48}
      shape="square"
      style={{
        borderRadius: 10,
        flexShrink: 0,
        background: tag?.rgbColor || '#f0f5ff',
        color: tag?.topLogo ? undefined : '#1890ff',
        fontSize: 22,
      }}
    />
  );

  const continueRoute = (clientId) => (
    tag?.teamId
      ? `/client/manage/${clientId}/teams/${tag.teamId}`
      : `/client/manage/${clientId}`
  );

  // ── Body variants ──
  let body = null;

  if (!tag) {
    body = (
      <Card style={{ borderRadius: 12, marginTop: 16 }}>
        <Title level={4}>Link not found</Title>
        <Paragraph type="secondary">
          We couldn&apos;t find this team link. Please check the link you were sent,
          or contact the salon owner.
        </Paragraph>
      </Card>
    );
  } else if (!xmid) {
    body = (
      <Card style={{ borderRadius: 12, marginTop: 16 }}>
        <Title level={4}>This join link is incomplete</Title>
        <Paragraph type="secondary">
          The link is missing an integration ID, so there is nothing to connect.
          Please use the full link you were sent, or contact the salon owner.
        </Paragraph>
      </Card>
    );
  } else if (!tag.teamId) {
    body = (
      <Card style={{ borderRadius: 12, marginTop: 16 }}>
        <Title level={4}>This team isn&apos;t set up yet</Title>
        <Paragraph type="secondary">
          This link isn&apos;t connected to a team on Striketip yet. Ask the salon
          owner to set up their team first, then try the link again.
        </Paragraph>
      </Card>
    );
  } else if (outcome && outcome.kind === 'success') {
    const data = outcome.data || {};
    const linkedId = data.xmid || xmid;
    const successClientId = data.clientId || client.id;
    body = (
      <Card style={{ borderRadius: 12, marginTop: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Space align="center" size={14}>
            <CheckCircleOutlined style={{ fontSize: 36, color: '#52c41a' }} />
            <div>
              <Title level={4} style={{ margin: 0 }}>You&apos;re linked</Title>
              <Text type="secondary">Your membership at {teamName} is connected.</Text>
            </div>
          </Space>
          <Paragraph style={{ margin: 0 }}>
            Reference: <Text strong>{linkedId}</Text>
          </Paragraph>
          {/*
            Three states, in the order they normally occur: setting up, taking a
            while, and ready. "Ready" is only ever on screen for the instant
            before the effect above navigates — it exists so the transition reads
            as an arrival rather than a jump.
          */}
          {!membershipReady && !setupSlow && (
            <Space align="center" size={12}>
              <Spin />
              <Text type="secondary">Setting up your team…</Text>
            </Space>
          )}
          {!membershipReady && setupSlow && (
            <Space direction="vertical" size={10} style={{ width: '100%' }}>
              <Text type="secondary">
                Your team is taking a moment to finish setting up. You&apos;re linked
                either way — you can carry on and it will appear shortly.
              </Text>
              <Button
                size="large"
                icon={<ArrowRightOutlined />}
                onClick={() => history.push(continueRoute(successClientId), { justJoined: true })}
                style={{ borderRadius: 8 }}
              >
                Continue
              </Button>
            </Space>
          )}
          {membershipReady && (
            <Space align="center" size={12}>
              <Spin />
              <Text type="secondary">Opening your team…</Text>
            </Space>
          )}
        </Space>
      </Card>
    );
  } else if (outcome && outcome.kind === 'paused') {
    body = (
      <Card style={{ borderRadius: 12, marginTop: 16 }}>
        <Alert
          type="warning"
          showIcon
          message="This team is currently paused"
          description="Linking is unavailable while the team is paused. Please try again later, or contact the salon owner."
          style={{ borderRadius: 8 }}
        />
      </Card>
    );
  } else if (outcome) {
    const ERROR_COPY = {
      'already-linked-different-id': 'This membership is already linked to a different ID for this integration — contact support.',
      'id-already-bound': 'This ID was just linked — contact your salon owner.',
      'membership-removed': "You've been removed from this team — contact the salon owner.",
    };
    const known = ERROR_COPY[outcome.kind];
    body = (
      <Card style={{ borderRadius: 12, marginTop: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Alert
            type="error"
            showIcon
            message={known ? "This ID can't be linked" : 'Something went wrong'}
            description={known || 'We couldn’t complete the link. Please try again.'}
            style={{ borderRadius: 8 }}
          />
          {!known && (
            <Button onClick={() => setOutcome(null)} style={{ borderRadius: 8 }}>
              Try again
            </Button>
          )}
        </Space>
      </Card>
    );
  } else {
    // ── Consent step ──
    //
    // Two voices for one button. A non-member is joining the team AND activating
    // the integration in one motion; an existing member is only activating. The
    // words must match what the API will actually do — it creates the membership
    // only when it does not already exist.
    //
    // Nothing here asks the reader to verify the xmid: it is the partner's
    // internal key, likely a UUID they have never seen. What they CAN verify is
    // the venue, the partner, the account they are signed in as — and that this
    // link was given to THEM. The hazard is a forwarded link, so that is what
    // the warning warns about.
    const signedInAs = client.displayName || client.email;
    const title = isMemberAlready
      ? (systemName
        ? `Activate ${systemName} tips for your membership?`
        : 'Activate tips for your membership?')
      : (systemName
        ? `Join ${teamName} and activate your ${systemName} tips?`
        : `Join ${teamName} and activate your tips?`);
    const explainer = isMemberAlready
      ? `You're already a member at ${teamName}. Connecting links `
        + `${recordHolder}'s record of you to your membership — from now on, `
        + 'tips left after your service are paid to your Striketip account '
        + `(signed in as ${signedInAs}).`
      : `This adds you to the team at ${teamName} and connects `
        + `${recordHolder}'s record of you to your Striketip account (signed in `
        + `as ${signedInAs}) — tips left after your service are paid to you.`;
    body = (
      <>
        <Alert
          type="warning"
          showIcon
          message={JOIN_GATE_COPY}
          description="If you were only trying to leave a tip, close this page and use the tipping link instead."
          style={{ borderRadius: 8, marginTop: 16, textAlign: 'left' }}
        />
        <Card style={{ borderRadius: 12, marginTop: 16 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space align="center" size={14}>
              {teamAvatar}
              <div>
                <Text strong style={{ fontSize: 16, display: 'block' }}>{teamName}</Text>
                <Text type="secondary">
                  {systemName ? `Staff link · via ${systemName}` : 'Staff link'}
                </Text>
              </div>
            </Space>
            {!isMemberAlready && (
              <Text type="secondary" style={{ fontSize: 14, fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                Step 3 of 3
              </Text>
            )}
            <Title level={4} style={{ margin: 0 }}>
              {title}
            </Title>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              {explainer}
            </Paragraph>
            <Paragraph type="secondary" style={{ margin: 0 }}>
              This link belongs to one staff member and connects once; only a
              manager can undo it. If it was forwarded to you by a colleague,
              don&apos;t continue — ask {systemName ? `${systemName} or ` : ''}your
              manager for your own.
            </Paragraph>
            <Button
              type="primary"
              size="large"
              icon={<LinkOutlined />}
              loading={joining}
              onClick={handleConfirmJoin}
              style={{ borderRadius: 8, backgroundColor: '#52c41a', borderColor: '#52c41a' }}
            >
              {isMemberAlready ? 'Activate' : 'Join and activate'}
            </Button>
          </Space>
        </Card>
        <div style={{ textAlign: 'center', marginTop: 24 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Reference: {xmid}
          </Text>
        </div>
      </>
    );
  }

  return (
    <div style={{ minHeight: '100%' }}>
      <div style={{ padding: '24px 24px 48px', maxWidth: 800, margin: '0 auto' }}>
        {body}
      </div>
    </div>
  );
}
